/**
 * @file HeaderFooterPanel.tsx
 * @description Панель «Шапка и подвал» (phase19.16 Р1; SB13, SB14, SB19,
 * SB73, SB90; uiux-design-r1 §2): раскладка и включение частей шапки и
 * подвала; тексты и картинки бренда — в «Настройках сайта» (кроме рисунка
 * подвала, который выбирается прямо здесь).
 *
 * Самостоятельная панель: документ оформления и бренд — общие части
 * каркаса; вкладка — это выбранная область общего предпросмотра: смена
 * вкладки прокручивает канву, клик по шапке или подвалу на канве
 * переключает вкладку.
 *
 * Партия «Юридические документы» (SB142): «Ссылки» подвала правят два
 * раздела темы — `footer.legal` и `footerDocs`; они уходят одним
 * изменением документа (два `update` подряд затёрли бы друг друга).
 */

import { useEffect } from "react";

import { Tabs } from "../../ui/Tabs";
import { SaveIndicator } from "../SaveIndicator";
import { useSiteView } from "../site-view-context";
import { UnsavedGuard } from "../UnsavedGuard";
import { FooterTab } from "./FooterTab";
import { HeaderTab } from "./HeaderTab";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";

type Tab = "header" | "footer";

interface Props {
  /** Вкладку задаёт конструктор (выбор в адресе); без неё — область общего предпросмотра. */
  tab?: Tab;
  onTab?: (tab: Tab) => void;
}

export function HeaderFooterPanel({ tab: outerTab, onTab }: Props = {}) {
  const { area, focus } = useSiteView().preview;
  const controlled = outerTab !== undefined && onTab !== undefined;
  // Вкладка = выбранная область предпросмотра: клик по подвалу на канве открывает «Подвал».
  const tab: Tab = controlled ? outerTab : (area ?? "header");
  const setTab = (next: Tab) => (controlled ? onTab(next) : focus({ area: next }));
  const themeDraft = useSiteDraftPart("theme");
  const brandDraft = useSiteDraftPart("brand");
  const { theme, update, change } = themeDraft;
  const unsaved = [themeDraft.status, brandDraft.status].some((s) => s === "error" || s === "conflict");

  // Открытие панели — канва к шапке (если область ещё не выбрана); уход — снять выбор.
  useEffect(() => {
    if (controlled) return;
    focus({ area: area ?? "header" });
    return () => focus({ area: null });
    // Только при открытии и закрытии панели.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Tabs
        aria-label="Шапка или подвал"
        items={[{ value: "header", label: "Шапка" }, { value: "footer", label: "Подвал" }]}
        value={tab}
        onChange={setTab}
      />
      <SaveIndicator state={themeDraft} />
      {brandDraft.status !== "idle" ? <SaveIndicator state={brandDraft} /> : null}
      {tab === "header" ? (
        <HeaderTab header={theme.header} brand={brandDraft.fields} colors={theme.colors} onChange={(patch) => update("header", patch)} />
      ) : (
        <FooterTab
          footer={theme.footer}
          brand={brandDraft.fields}
          colors={theme.colors}
          onChange={(patch) => update("footer", patch)}
          onDecor={(media) => brandDraft.set("footerDecor", media)}
          footerDocs={theme.footerDocs}
          onLinks={({ legal, docs }) =>
            change(
              { ...theme, ...(legal ? { footer: { ...theme.footer, legal } } : {}), ...(docs ? { footerDocs: docs } : {}) },
              [...(legal ? ["footer"] : []), ...(docs ? ["footerDocs"] : [])],
            )
          }
        />
      )}
      <UnsavedGuard when={unsaved} />
    </div>
  );
}
